import classnames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Scrollbars, Tabs, Tooltip } from '@meitu/saas-ui';
import { ITabItemProps } from '@meitu/saas-ui/es/tabs/interface';
import { BackTitle, SimpleTitle } from '@meitu/saas-ui/es/title';
import { ocrImageFileToText, parseQRCodeToText } from '@meitu/saas-editor-core/es/layers/qr-code-layer';
import { CollapseType } from '@meitu/saas-editor-ui/es/types';
import { getLocaleStore, useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { getWxOfficialQrCode } from '@/services/qrcode';
import { useCollapseModel } from '@/store/collapse-models/collapse-model';
import { codeInfo, codeListConf, getQrCodeImage, qrCodeCoverList } from './config';
import './qr-code.less';
import IconFont from '@meitu/xdesign-icon-font';
import { events } from '@/events-bus';
import { PLUGIN_CLOSE_EVENT } from '../../constants';

export const QR_CODE = 'qr-code';
enum TabType {
    text = 'text',
    official = 'official',
}

const TAB_DATA: () => ITabItemProps[] = () => {
    const { $t } = getLocaleStore();

    return [
        {
            name: $t((v) => v.collapse.moreCollapse.componentTool.qrCode.website),
            tabKey: TabType.text,
        },
        {
            name: $t((v) => v.collapse.moreCollapse.componentTool.qrCode.official),
            tabKey: TabType.official,
        },
    ];
};

const StyleList: React.FC<{ onChange?: (index: number) => void }> = React.memo(({ onChange }) => {
    const { $t } = useLocaleStore();
    const [activeIndex, setActiveIndex] = useState(0);
    return (
        <>
            <div style={{ height: 28 }}></div>
            <SimpleTitle text={$t((v) => v.collapse.moreCollapse.componentTool.qrCode.needSelected)} fontSize={14} />
            <div className="plugin-qrcode-list">
                {qrCodeCoverList.map((item, index) => {
                    return (
                        <div
                            key={index}
                            onClick={() => {
                                setActiveIndex(index);
                                onChange?.(index);
                            }}
                            className={classnames('plugin-qrcode-item', {
                                active: activeIndex === index,
                            })}
                        >
                            <div className="plugin-qrcode-item-wrap">{getQrCodeImage(index, 58, 58)}</div>
                        </div>
                    );
                })}
            </div>
        </>
    );
});

const QrCodeCollapse: React.FC = () => {
    const { $t } = useLocaleStore();
    const currentType = useCollapseModel((state) => state.currentType);
    const [tabType, setTabType] = useState<TabType>(TabType.text);
    const [text, setText] = useState('');
    const [official, setOfficial] = useState('');
    const activeIndex = useRef(0);
    const selectFile = useRef<any>(null);
    const [isOcr, setIsOcr] = useState(false);
    const [toolTip, setToolTip] = useState({ open: false, text: '' });

    useEffect(() => {
        if (currentType !== CollapseType.SHORTCUT_MENU) {
            setToolTip({ open: false, text: '' });
        }
        if (toolTip.open) {
            setTimeout(() => {
                setToolTip((pre) => {
                    return {
                        ...pre,
                        open: false,
                    };
                });
            }, 2000);
        }
    }, [toolTip.open, currentType]);

    // 样式选择
    const onStyleChange = useCallback((index: number) => {
        activeIndex.current = index;
    }, []);

    const createQrCodeByUrl = () => {
        const style = codeListConf[activeIndex.current];
        editor.qrCodeLayer.addByInfo(
            {
                text: text || codeInfo().text,
                official: '',
                fill: '#ffffffff',
                ...style,
            },
            {
                trackProps: 'text',
            },
        );
    };

    const createQrCodeByOfficial = () => {
        const finalOfficial = official || codeInfo().official;
        getWxOfficialQrCode(finalOfficial)
            .then((res) => {
                const base64 = res.data.data.base64;
                parseQRCodeToText(`data:image/jpeg;base64,${base64}`).then((text: string | undefined) => {
                    if (!text) {
                        setToolTip({
                            open: true,
                            text: $t(
                                (v) => v.collapse.moreCollapse.componentTool.qrCode.messageError.officialInputError,
                            ),
                        });
                    } else {
                        setText(text);
                        const style = codeListConf[activeIndex.current];
                        editor.qrCodeLayer.addByInfo(
                            {
                                text,
                                official: finalOfficial,
                                fill: '#ffffffff',
                                ...style,
                            },
                            {
                                trackProps: 'official',
                            },
                        );
                    }
                });
            })
            .catch(() => {
                setToolTip({
                    open: true,
                    text: $t((v) => v.collapse.moreCollapse.componentTool.qrCode.messageError.officialInputError),
                });
            });
    };

    // 上传识别二维码
    const fileOcrHandle = (e: any) => {
        if (!e.target.files?.[0]) return;
        setIsOcr(true);
        ocrImageFileToText(e.target.files?.[0]).then((text: string) => {
            if (text) {
                setText(text);
            } else {
                setToolTip({
                    open: true,
                    text: $t((v) => v.collapse.moreCollapse.componentTool.qrCode.messageError.recognitionFailed),
                });
            }
            console.log('text', text);
            setIsOcr(false);
        });
    };
    return (
        <Scrollbars autoHide>
            <div className="plugin-qrcode">
                <BackTitle
                    text={$t((v) => v.collapse.moreCollapse.componentTool.qrCode.title)}
                    handleBackClick={() => {
                        events.emit(PLUGIN_CLOSE_EVENT);
                    }}
                />
                <div style={{ height: 16 }}></div>
                {isIntl ? (
                    <div className="plugin-qrcode-tipText">
                        {$t((v) => v.collapse.moreCollapse.componentTool.qrCode.intlTabTip)}
                    </div>
                ) : (
                    <Tabs
                        data={TAB_DATA()}
                        tabsHeight="34px"
                        handleTabClick={(key) => {
                            setTabType(key as TabType);
                            setToolTip({
                                open: false,
                                text: '',
                            });
                        }}
                    />
                )}

                {tabType === TabType.text ? (
                    <div className="plugin-qrcode-ocr">
                        <Tooltip
                            open={toolTip.open}
                            title={toolTip.text}
                            overlayClassName={classnames({
                                'plugin-qrcode-ocr-tooltips': toolTip.text.length > 14,
                            })}
                        >
                            <Input
                                key="text"
                                value={text}
                                className={classnames('plugin-qrcode-input', {
                                    'plugin-qrcode-input-warning': toolTip.open,
                                })}
                                placeholder={codeInfo().text}
                                onChange={(e) => {
                                    let { value } = e.target;
                                    value = value.trimStart();
                                    if (value === text) return;
                                    setText(value);
                                }}
                                style={{ width: 204 }}
                            />
                        </Tooltip>
                        <Tooltip
                            placement="bottom"
                            title={$t((v) => v.collapse.moreCollapse.componentTool.qrCode.uploadQrCode)}
                        >
                            <div
                                onClick={() => {
                                    if (isOcr) return;
                                    selectFile.current?.click();
                                }}
                                className={classnames('plugin-qrcode-ocr-icon', {
                                    'plugin-qrcode-ocr-loading': isOcr,
                                })}
                            >
                                {isOcr ? (
                                    <IconFont name="LoadingCircleBold" fontSize={16} />
                                ) : (
                                    <IconFont name="PictureAddBold" fontSize={16} />
                                )}
                            </div>
                        </Tooltip>
                        <input
                            accept="image/jpeg, image/png, image/gif"
                            type="file"
                            style={{ width: 0, height: 0, opacity: 0 }}
                            ref={selectFile}
                            onChange={fileOcrHandle}
                        />
                    </div>
                ) : (
                    <div className="plugin-qrcode-ocr">
                        <Tooltip open={toolTip.open} title={toolTip.text}>
                            <Input
                                key="official"
                                value={official}
                                className={classnames('plugin-qrcode-input', {
                                    'plugin-qrcode-input-warning': toolTip.open,
                                })}
                                placeholder={codeInfo().official}
                                onChange={(e) => {
                                    let { value } = e.target;
                                    value = value.trimStart();
                                    if (value === official) return;
                                    setOfficial(value);
                                }}
                            />
                        </Tooltip>
                    </div>
                )}
                <StyleList onChange={onStyleChange} />

                <div className="plugin-qrcode-btns">
                    <Button
                        key="text"
                        type="primary"
                        data-blue
                        block
                        onClick={() => {
                            if (tabType === TabType.text) {
                                createQrCodeByUrl();
                            } else {
                                createQrCodeByOfficial();
                            }
                        }}
                    >
                        {$t((v) => v.collapse.moreCollapse.componentTool.qrCode.createQrCode)}
                    </Button>
                </div>
            </div>
        </Scrollbars>
    );
};

export default React.memo(QrCodeCollapse);
